import React, { useEffect, useState, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Video, VideoOff, Users, Loader2, PhoneOff, Mic, MicOff, MonitorUp, MonitorOff, Circle } from 'lucide-react';
import axios from 'axios';
import {
    LiveKitRoom,
    GridLayout,
    ParticipantTile,
    RoomAudioRenderer,
    useParticipants,
    useTracks,
    useLocalParticipant,
    useRoomContext
} from '@livekit/components-react';
import '@livekit/components-styles';
import { Track } from 'livekit-client';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ConferenceView = ({
    socket,
    popupId,
    user,
    isAdmin,
    liveSession,
    onEndSession
}) => {
    const [token, setToken] = useState(null);
    const [serverUrl, setServerUrl] = useState(null);
    const [isConnecting, setIsConnecting] = useState(false);
    const [error, setError] = useState(null);
    const [isJoined, setIsJoined] = useState(false);
    const [isRecording, setIsRecording] = useState(false);
    const [recordingStartedAt, setRecordingStartedAt] = useState(null);

    // Start backend recording (LiveKit Egress to S3)
    const startRecording = async () => {
        try {
            console.log('[ConferenceView] Starting LiveKit Egress recording...');
            const response = await axios.post(`${BACKEND_URL}/api/recording/start`, {
                room_name: popupId,
                popup_id: popupId
            });

            if (response.data.success) {
                setIsRecording(true);
                setRecordingStartedAt(new Date());
                console.log('[ConferenceView] Recording started, egress_id:', response.data.egress_id);
            }
        } catch (err) {
            console.error('[ConferenceView] Error starting recording:', err);
            // Don't fail the session if recording fails - show warning instead
            console.warn('[ConferenceView] Recording unavailable, continuing without recording');
        }
    };

    // Stop backend recording and get S3 key
    const stopRecording = async () => {
        try {
            console.log('[ConferenceView] Stopping LiveKit Egress recording...');
            const response = await axios.post(`${BACKEND_URL}/api/recording/stop`, {
                popup_id: popupId
            });

            if (response.data.success) {
                console.log('[ConferenceView] Recording stopped, S3 key:', response.data.s3_key);
                return response.data.s3_key;
            }
        } catch (err) {
            console.error('[ConferenceView] Error stopping recording:', err);
        }
        return null;
    };

    // Fetch LiveKit token when joining
    const joinConference = useCallback(async () => {
        setIsConnecting(true);
        setError(null);

        try {
            console.log('[ConferenceView] Getting token from backend...');
            const response = await axios.post(`${BACKEND_URL}/api/livekit/token`, {
                room_name: popupId,
                participant_name: user.username,
                participant_id: user.id
            });

            console.log('[ConferenceView] Token response:', response.data);

            if (!response.data.token) {
                throw new Error('No token received');
            }

            if (!response.data.serverUrl) {
                throw new Error('No LiveKit server URL received');
            }

            setToken(response.data.token);
            setServerUrl(response.data.serverUrl);
            setIsJoined(true);

            socket.emit('join_live_session', {
                popup_id: popupId,
                user_id: user.id
            });

            console.log('[ConferenceView] Successfully got token, connecting to:', response.data.serverUrl);

            // Start recording for admin
            if (isAdmin) {
                // Delay recording start to let room connect first
                setTimeout(() => startRecording(), 3000);
            }
        } catch (err) {
            console.error('[ConferenceView] Error getting LiveKit token:', err);
            setError(err.response?.data?.detail || err.message || 'Failed to join conference. Please try again.');
        } finally {
            setIsConnecting(false);
        }
    }, [popupId, user, socket, isAdmin]);

    // Auto-join for admin
    useEffect(() => {
        if (isAdmin && !isJoined && !token) {
            joinConference();
        }
    }, [isAdmin, isJoined, token, joinConference]);

    const handleDisconnect = useCallback(async () => {
        console.log('[ConferenceView] Disconnecting...');

        // Stop recording if admin and recording is active
        let s3Key = null;
        if (isAdmin && isRecording) {
            s3Key = await stopRecording();
        }

        setToken(null);
        setServerUrl(null);
        setIsJoined(false);
        setIsRecording(false);

        socket.emit('leave_live_session', {
            popup_id: popupId,
            user_id: user.id
        });

        if (isAdmin) {
            // Pass S3 key to parent for save dialog
            onEndSession(s3Key);
        }
    }, [socket, popupId, user.id, isAdmin, onEndSession, isRecording]);

    // Show join button for non-admin users who haven't joined yet
    if (!isJoined && !isAdmin) {
        return (
            <div className="flex flex-col items-center justify-center h-full bg-gradient-to-b from-[#1a0a0e] to-[#2e0510] rounded-xl p-8">
                <div className="text-center mb-6">
                    <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-[#FFC857]/20 flex items-center justify-center">
                        <Users className="w-10 h-10 text-[#FFC857]" />
                    </div>
                    <h3 className="text-xl font-bold text-[#F5E3D7] mb-2">Conference in Progress</h3>
                    <p className="text-[#F5E3D7]/60">Join to share your video and audio</p>
                </div>
                {error && (
                    <p className="text-red-400 text-sm mb-4 text-center max-w-xs">{error}</p>
                )}
                <Button
                    onClick={joinConference}
                    disabled={isConnecting}
                    className="bg-[#FFC857] text-[#2e0510] hover:bg-[#FFD87A] px-8 py-3"
                >
                    {isConnecting ? (
                        <>
                            <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                            Connecting...
                        </>
                    ) : (
                        <>
                            <Video className="w-5 h-5 mr-2" />
                            Join Conference
                        </>
                    )}
                </Button>
            </div>
        );
    }

    // Show loading while connecting
    if (isConnecting || !token || !serverUrl) {
        return (
            <div className="flex flex-col items-center justify-center h-full bg-gradient-to-b from-[#1a0a0e] to-[#2e0510] rounded-xl p-8">
                <Loader2 className="w-12 h-12 text-[#FFC857] animate-spin mb-4" />
                <p className="text-[#F5E3D7]">Connecting to conference...</p>
                {error && (
                    <p className="text-red-400 text-sm mt-4 text-center max-w-xs">{error}</p>
                )}
            </div>
        );
    }

    return (
        <div className="relative h-full bg-gradient-to-b from-[#1a0a0e] to-[#2e0510] rounded-xl overflow-hidden">
            <LiveKitRoom
                token={token}
                serverUrl={serverUrl}
                connect={true}
                video={true}
                audio={true}
                onDisconnected={handleDisconnect}
                style={{ height: '100%' }}
            >
                <ConferenceContent
                    onLeave={handleDisconnect}
                    isRecording={isRecording}
                    recordingStartedAt={recordingStartedAt}
                />
            </LiveKitRoom>
        </div>
    );
};

// Inner component that has access to LiveKit room context
const ConferenceContent = ({ onLeave, isRecording, recordingStartedAt }) => {
    const participants = useParticipants();
    const { localParticipant } = useLocalParticipant();

    const [isMicOn, setIsMicOn] = useState(true);
    const [isCameraOn, setIsCameraOn] = useState(true);
    const [isScreenSharing, setIsScreenSharing] = useState(false);
    const [recordingDuration, setRecordingDuration] = useState('00:00');

    // Update recording duration
    useEffect(() => {
        if (!isRecording || !recordingStartedAt) return;

        const interval = setInterval(() => {
            const seconds = Math.floor((new Date() - recordingStartedAt) / 1000);
            const mins = Math.floor(seconds / 60).toString().padStart(2, '0');
            const secs = (seconds % 60).toString().padStart(2, '0');
            setRecordingDuration(`${mins}:${secs}`);
        }, 1000);

        return () => clearInterval(interval);
    }, [isRecording, recordingStartedAt]);

    // Get video tracks for the grid
    const tracks = useTracks(
        [
            { source: Track.Source.Camera, withPlaceholder: true },
            { source: Track.Source.ScreenShare, withPlaceholder: false },
        ],
        { onlySubscribed: false }
    );

    const toggleMic = async () => {
        try {
            await localParticipant.setMicrophoneEnabled(!isMicOn);
            setIsMicOn(!isMicOn);
        } catch (err) {
            console.error('Error toggling mic:', err);
        }
    };

    const toggleCamera = async () => {
        try {
            await localParticipant.setCameraEnabled(!isCameraOn);
            setIsCameraOn(!isCameraOn);
        } catch (err) {
            console.error('Error toggling camera:', err);
        }
    };

    const toggleScreenShare = async () => {
        try {
            await localParticipant.setScreenShareEnabled(!isScreenSharing);
            setIsScreenSharing(!isScreenSharing);
        } catch (err) {
            console.error('Error toggling screen share:', err);
        }
    };

    return (
        <div className="flex flex-col h-full">
            {/* Header */}
            <div className="absolute top-4 left-4 z-10 flex items-center gap-3">
                <div className="flex items-center gap-2 px-3 py-1.5 bg-green-600 rounded-full">
                    <div className="w-2 h-2 bg-white rounded-full animate-pulse" />
                    <span className="text-white text-sm font-semibold">LIVE</span>
                </div>
                <div className="flex items-center gap-1 px-3 py-1.5 bg-[#2e0510]/80 rounded-full border border-[#3d1a24]">
                    <Users className="w-4 h-4 text-[#FFC857]" />
                    <span className="text-[#F5E3D7] text-sm">{participants.length}</span>
                </div>

                {/* Recording indicator */}
                {isRecording && (
                    <div className="flex items-center gap-2 px-3 py-1.5 bg-red-600 rounded-full">
                        <Circle className="w-3 h-3 text-white fill-white animate-pulse" />
                        <span className="text-white text-sm font-semibold">REC {recordingDuration}</span>
                    </div>
                )}
            </div>

            {/* Video Grid - Only video tiles, no chat */}
            <div className="flex-1 p-4 pt-16 pb-20">
                <GridLayout tracks={tracks} style={{ height: '100%' }}>
                    <ParticipantTile />
                </GridLayout>
            </div>

            {/* Custom Controls Bar */}
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-3 p-3 bg-[#1a0a0e]/90 backdrop-blur-sm rounded-full border border-[#3d1a24]">
                {/* Mic Toggle */}
                <Button
                    onClick={toggleMic}
                    className={`w-12 h-12 rounded-full transition-all ${isMicOn
                            ? 'bg-[#3d1a24] hover:bg-[#4d2a34] text-[#F5E3D7]'
                            : 'bg-red-600 hover:bg-red-700 text-white'
                        }`}
                    title={isMicOn ? 'Mute Microphone' : 'Unmute Microphone'}
                >
                    {isMicOn ? <Mic className="w-5 h-5" /> : <MicOff className="w-5 h-5" />}
                </Button>

                {/* Camera Toggle */}
                <Button
                    onClick={toggleCamera}
                    className={`w-12 h-12 rounded-full transition-all ${isCameraOn
                            ? 'bg-[#3d1a24] hover:bg-[#4d2a34] text-[#F5E3D7]'
                            : 'bg-red-600 hover:bg-red-700 text-white'
                        }`}
                    title={isCameraOn ? 'Turn Off Camera' : 'Turn On Camera'}
                >
                    {isCameraOn ? <Video className="w-5 h-5" /> : <VideoOff className="w-5 h-5" />}
                </Button>

                {/* Screen Share Toggle */}
                <Button
                    onClick={toggleScreenShare}
                    className={`w-12 h-12 rounded-full transition-all ${isScreenSharing
                            ? 'bg-[#FFC857] hover:bg-[#FFD87A] text-[#2e0510]'
                            : 'bg-[#3d1a24] hover:bg-[#4d2a34] text-[#F5E3D7]'
                        }`}
                    title={isScreenSharing ? 'Stop Sharing' : 'Share Screen'}
                >
                    {isScreenSharing ? <MonitorOff className="w-5 h-5" /> : <MonitorUp className="w-5 h-5" />}
                </Button>

                {/* Divider */}
                <div className="w-px h-8 bg-[#3d1a24]" />

                {/* Leave Button */}
                <Button
                    onClick={onLeave}
                    className="w-12 h-12 rounded-full bg-red-600 hover:bg-red-700 text-white transition-all"
                    title="Leave Conference"
                >
                    <PhoneOff className="w-5 h-5" />
                </Button>
            </div>

            {/* Audio renderer (invisible but needed) */}
            <RoomAudioRenderer />
        </div>
    );
};

export default ConferenceView;
